import { PATH } from "./paths";
import { USER_ROLE } from "../Types/user";
import { getTutorialDemoAccountPath } from "./tutorialDemoData";
import { TUTORIAL_SCENARIO } from "./tutorialScenario";
import type { TooltipSize } from "../utils/tutorialTooltipPosition";

/** `data-tour` attribute values used across the app shell. */
export const TOUR_TARGET = {
  NAV_HOTLIST: "nav-hotlist",
  NAV_ACCOUNTS: "nav-accounts",
  NAV_INSIGHTS: "nav-insights",
  NAV_ASSETS: "nav-assets",
  NAV_SETTINGS: "nav-settings",
  HOTLIST_HEADER: "hotlist-header",
  HOTLIST_SCHEDULE: "hotlist-schedule",
  HOTLIST_VIEW_SWITCH: "hotlist-view-switch",
  HOTLIST_TABLE: "hotlist-table",
  HOTLIST_TABLE_HEAD: "hotlist-table-head",
  HOTLIST_COLUMN_PICKER: "hotlist-column-picker",
  HOTLIST_TABLE_ROW: "hotlist-table-row",
  HOTLIST_REP_SCOPE: "hotlist-rep-scope",
  ACCOUNTS_TOOLBAR: "accounts-toolbar",
  ACCOUNTS_SEARCH: "accounts-search",
  ACCOUNTS_ADD: "accounts-add",
  ACCOUNTS_TABLE: "accounts-table",
  ACCOUNTS_TABLE_HEAD: "accounts-table-head",
  ACCOUNTS_ROW_DETAIL: "accounts-row-detail",
  ACCOUNTS_BULK_ASSIGN: "accounts-bulk-assign",
  ACCOUNTS_ROW_ASSIGN: "accounts-row-assign",
  CAL_ACTION_INSIGHT: "cal-action-insight",
  CAL_ACTION_PROMPT: "cal-action-prompt",
  CAL_ACTION_FOOTER: "cal-action-footer-cta",
  SOCIAL_PROMPT_SEQUENCE: "social-prompt-sequence",
  SOCIAL_PROMPT_COMPOSE: "social-prompt-compose",
  SOCIAL_PROMPT_CONTACTS: "social-prompt-contacts",
  SOCIAL_PROMPT_FOOTER: "social-prompt-footer",
  SOCIAL_PROMPT_REQUEST_MORE: "social-prompt-request-more",
  SOCIAL_PROMPT_REQUEST_SETUP: "social-prompt-request-setup",
  SOCIAL_PROMPT_REQUEST_CADENCE: "social-prompt-request-cadence",
  SOCIAL_PROMPT_REQUEST_RECIPIENT: "social-prompt-request-recipient",
  SOCIAL_PROMPT_REQUEST: "social-prompt-request",
  OUTREACH_SHARE: "outreach-share",
  OUTREACH_COMPLETE: "outreach-complete",
  OUTREACH_DISMISS: "outreach-dismiss",
  INSIGHT_COMPLETE_MODAL: "insight-complete-modal",
  INSIGHT_DISMISS_MODAL: "insight-dismiss-modal",
  ACCOUNT_MASTHEAD: "account-masthead",
  ACCOUNT_MASTHEAD_META: "account-masthead-meta",
  ACCOUNT_TAB_SWITCH: "account-tab-switch",
  ACCOUNT_CALENDAR_TAB: "account-calendar-tab",
  ACCOUNT_CALENDAR_ACTION: "account-calendar-action",
  ACCOUNT_CALENDAR_VIEW_TOGGLE: "account-calendar-view-toggle",
  ACCOUNT_INSIGHTS_TAB: "account-insights-tab",
  ACCOUNT_SIGNALS_NAV: "account-signals-nav",
  ACCOUNT_SIGNALS_ACTIONS: "account-signals-actions",
  ACCOUNT_INSIGHTS_VIEW_TOGGLE: "account-insights-view-toggle",
  ACCOUNT_INSIGHTS_ROW_DETAIL: "account-insights-row-detail",
  ACCOUNT_INSIGHTS_TABLE_ACTIONS: "account-insights-table-actions",
  ACCOUNT_CONTACTS_TAB: "account-contacts-tab",
  ACCOUNT_CONTACTS_ROW_DETAIL: "account-contacts-row-detail",
  ACCOUNT_CONTACTS_ACTIONS: "account-contacts-actions",
  ACCOUNT_CONTACTS_REORDER: "account-contacts-reorder",
  ACCOUNT_CONTACTS_SOCIAL: "account-contacts-social",
  ACCOUNT_WORKSPACE: "account-workspace",
  ACCOUNT_INSIGHTS_HEAD: "account-insights-head",
  ACCOUNT_CONTACTS_HEAD: "account-contacts-head",
  INSIGHTS_WORKSPACE: "insights-workspace",
  ASSETS_MAIN: "assets-main",
  ASSETS_TABLE_HEAD: "assets-table-head",
  ASSETS_SOCIAL_PROMPT: "assets-social-prompt",
  ASSETS_ADD: "assets-add",
  ASSETS_ROW_DETAIL: "assets-row-detail",
  SETTINGS_QUICK_ACTIONS: "settings-quick-actions",
  SETTINGS_PREFERENCES_GROUP: "settings-preferences-group",
  SETTINGS_ORGANIZATION_GROUP: "settings-organization-group",
  SETTINGS_DIGEST: "settings-digest",
  SETTINGS_NOTIFICATIONS: "settings-notifications",
  SETTINGS_OUTREACH: "settings-outreach",
  SETTINGS_CADENCE_TEMPLATES: "settings-cadence-templates",
  SETTINGS_CLIENT_SETUP: "settings-client-setup",
  SETTINGS_ORG_DIGEST: "settings-org-digest",
  SETTINGS_TEAM: "settings-team",
  SETTINGS_ACCESS: "settings-access",
  SETTINGS_BILLING: "settings-billing",
} as const;

export type TourTarget = (typeof TOUR_TARGET)[keyof typeof TOUR_TARGET];

export type TutorialStepId =
  | "welcome"
  | "nav-hotlist"
  | "hotlist-actions"
  | "hotlist-schedule"
  | "hotlist-view-switch"
  | "hotlist-work-action"
  | "hotlist-column-picker"
  | "hotlist-table-rows"
  | "hotlist-action-context"
  | "hotlist-action-prompt"
  | "hotlist-action-footer"
  | "team-rep-filter"
  | "nav-accounts"
  | "accounts-portfolio"
  | "accounts-search"
  | "accounts-add"
  | "accounts-open"
  | "accounts-row-expand"
  | "accounts-bulk-assign"
  | "accounts-row-assign"
  | "accounts-enter"
  | "account-masthead-meta"
  | "account-calendar-tab"
  | "account-calendar-action"
  | "account-calendar-view-toggle"
  | "account-insights-tab"
  | "account-signals-overview"
  | "account-signals-actions"
  | "account-insights-view-toggle"
  | "account-insights-table"
  | "account-insights-row-expand"
  | "account-insights-table-actions"
  | "account-contacts-tab"
  | "account-contacts"
  | "account-contacts-row-expand"
  | "account-contacts-actions"
  | "account-contacts-reorder"
  | "account-contacts-social"
  | "outreach-icons"
  | "social-prompt-sequence"
  | "social-prompt-compose"
  | "social-prompt-contacts"
  | "social-prompt-footer"
  | "social-prompt-request-more"
  | "social-prompt-request-setup"
  | "social-prompt-request-cadence"
  | "social-prompt-request-recipient"
  | "social-prompt-request"
  | "insight-complete-icon"
  | "insight-complete"
  | "insight-dismiss-icon"
  | "insight-dismiss"
  | "nav-insights"
  | "insights-overview"
  | "insights-library"
  | "insights-competitors"
  | "nav-assets"
  | "assets-library"
  | "assets-row-expand"
  | "assets-social-prompt"
  | "assets-add"
  | "nav-settings"
  | "settings-quick-actions"
  | "settings-preferences"
  | "settings-digest"
  | "settings-notifications"
  | "settings-outreach"
  | "settings-cadence-templates"
  | "settings-organization"
  | "settings-client-setup"
  | "settings-org-digest"
  | "settings-access"
  | "settings-team"
  | "settings-billing"
  | "finish";

export type TutorialStepKind = "center" | "spotlight";

export type TutorialPlacement = "top" | "bottom" | "left" | "right";

export type TutorialDemoOverlay =
  | "social-prompt"
  | "social-prompt-request"
  | "complete-insight"
  | "dismiss-insight";

export interface TutorialStep {
  id: TutorialStepId;
  roles: USER_ROLE[];
  kind: TutorialStepKind;
  /** Navigate here before highlighting (spotlight steps). May include search params. */
  route?: string;
  /** Matches `[data-tour="…"]` on a DOM node. */
  target?: TourTarget;
  placement?: TutorialPlacement;
  /** When true, clicking the highlighted element advances to the next step. */
  advanceOnClick?: boolean;
  /** Real modal / workspace rendered under the tour while this step is active. */
  demoOverlay?: TutorialDemoOverlay;
  /** Settings section id (or other collapsible) to expand before measuring. */
  expandSection?: string;
  /** Compact guide card for tight layouts (tables, settings rows). */
  tooltipSize?: TooltipSize;
  /**
   * Keep the guide card on the left/right of the target (never top/bottom).
   * Used for centered demo modals where vertical fallback covers the dialog.
   */
  tooltipPreferSide?: boolean;
  /** Cap spotlight height for tall targets (e.g. settings panels). */
  spotlightClipHeight?: number;
  /** Cap spotlight width for targets that measure wider than their visual rail. */
  spotlightClipWidth?: number;
  /** Override default spotlight padding around the target. */
  spotlightPad?: number;
  /** How `scrollIntoView` aligns the target (`center` by default). */
  scrollBlock?: ScrollLogicalPosition;
  /** When true, skip scrolling the target into view (keep current viewport). */
  skipScroll?: boolean;
  kicker: string;
  title: string;
  subtitle: string;
  body: string;
  bullets: string[];
  icon: string;
  accent: string;
  accentSoft: string;
}

const APP_ROLES: USER_ROLE[] = [
  USER_ROLE.SALES_REP,
  USER_ROLE.SALES_LEADER,
  USER_ROLE.CLIENT_ADMIN,
  USER_ROLE.ADMIN,
  USER_ROLE.USER,
];

const LEADER_ROLES: USER_ROLE[] = [
  USER_ROLE.SALES_LEADER,
  USER_ROLE.CLIENT_ADMIN,
  USER_ROLE.ADMIN,
];

const ADMIN_ROLES: USER_ROLE[] = [USER_ROLE.CLIENT_ADMIN, USER_ROLE.ADMIN];

export const TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: "welcome",
    roles: APP_ROLES,
    kind: "center",
    kicker: "Welcome",
    title: "Choose what you want to learn",
    subtitle: "Take the full walkthrough, or jump into one area.",
    body:
      "This guide uses a realistic sample workspace — your live data stays untouched. Pick a path below to get started.",
    bullets: [
      "Full tour walks Hotlist, Accounts, Outreach, Insights, Assets, and Settings in order.",
      "Shorter paths skip everything else so you can finish in a few minutes.",
      "Reopen anytime from App Guide at the bottom of the sidebar.",
    ],
    icon: "fa-sparkles",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.1)",
  },
  {
    id: "nav-hotlist",
    roles: APP_ROLES,
    kind: "spotlight",
    target: TOUR_TARGET.NAV_HOTLIST,
    placement: "right",
    kicker: "Hotlist",
    title: "Start here every morning",
    subtitle: "Your daily action queue lives here.",
    body:
      "Hotlist is where Intentico stacks your highest-priority emails, calls, LinkedIn touches, and insight follow-ups. Look at the highlighted item — we'll open the Hotlist on the next step.",
    bullets: [
      "You'll return here throughout the day to work your queue.",
      "The count in the header shows how many actions are scheduled.",
      "Press Next when you're ready to see the Hotlist page.",
    ],
    icon: "fa-list-check",
    accent: "var(--color-hotlist)",
    accentSoft: "rgba(var(--rgb-hotlist), 0.1)",
  },
  {
    id: "hotlist-actions",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.Sales_Home,
    target: TOUR_TARGET.HOTLIST_HEADER,
    placement: "bottom",
    kicker: "Hotlist",
    title: "Your action queue for the week",
    subtitle: "The channel counts show how much is on your plate.",
    body:
      "The toolbar summarizes actions for your selected day or full week. Choose a channel on the left or adjust minimum priority on the right.",
    bullets: [
      "Priority and type filters help you focus on what's urgent.",
      "Thought-leadership prompts appear here when enabled for your org.",
    ],
    icon: "fa-calendar-week",
    accent: "var(--color-hotlist)",
    accentSoft: "rgba(var(--rgb-hotlist), 0.1)",
  },
  {
    id: "team-rep-filter",
    roles: LEADER_ROLES,
    kind: "spotlight",
    route: PATH.Sales_Home,
    target: TOUR_TARGET.HOTLIST_REP_SCOPE,
    placement: "bottom",
    kicker: "For leaders",
    title: "Filter by account or rep",
    subtitle: "Narrow the Hotlist to a seller or a company.",
    body:
      "Use the filters in the Company and Assignee column headers to focus the Hotlist by account, rep, or both. Show a hidden column from the column picker to access its filter.",
    bullets: [
      "Filter by account to see one company's actions.",
      "Filter by rep to coach an individual seller's queue.",
      "The same filters appear on Accounts.",
    ],
    icon: "fa-people-group",
    accent: "var(--action-today-badge)",
    accentSoft: "rgba(var(--rgb-teal), 0.1)",
  },
  {
    id: "hotlist-schedule",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.Sales_Home,
    target: TOUR_TARGET.HOTLIST_SCHEDULE,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Schedule",
    title: "Change the day you're working",
    subtitle: "Pick a day or view the full week.",
    body:
      "Open this control to jump to another day this week, return to Today, or switch on Full week to see Mon–Sun in one list.",
    bullets: [
      "Day view focuses on a single date; Full week shows everything scheduled.",
      "Your Hotlist updates as soon as you pick a different day.",
    ],
    icon: "fa-calendar-days",
    accent: "var(--color-hotlist)",
    accentSoft: "rgba(var(--rgb-hotlist), 0.1)",
  },
  {
    id: "hotlist-view-switch",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.Sales_Home,
    target: TOUR_TARGET.HOTLIST_VIEW_SWITCH,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Views",
    title: "Hotlist or action history",
    subtitle: "Toggle between today's queue and past work.",
    body:
      "The calendar icon keeps you on the live Hotlist. Switch to the clock icon to review completed actions and past activity.",
    bullets: [
      "Use Hotlist for what you still need to do.",
      "Use Action history when you want to look back at finished touches.",
    ],
    icon: "fa-clock-rotate-left",
    accent: "var(--color-hotlist)",
    accentSoft: "rgba(var(--rgb-hotlist), 0.1)",
  },
  {
    id: "hotlist-work-action",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.Sales_Home,
    target: TOUR_TARGET.HOTLIST_TABLE_HEAD,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Your queue",
    title: "Hotlist columns",
    subtitle: "What each column tells you.",
    body:
      `The header row labels date, action type, signal, account, and priority. Sample rows below are from the ${TUTORIAL_SCENARIO.clientLabel} tour snapshot.`,
    bullets: [
      "Sort columns to change how actions are ordered.",
      "Press Next to customize which columns you see.",
    ],
    icon: "fa-table-list",
    accent: "var(--color-hotlist)",
    accentSoft: "rgba(var(--rgb-hotlist), 0.1)",
  },
  {
    id: "hotlist-column-picker",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.Sales_Home,
    target: TOUR_TARGET.HOTLIST_COLUMN_PICKER,
    placement: "left",
    tooltipSize: "compact",
    kicker: "Your queue",
    title: "Show or hide columns",
    subtitle: "Customize the table to fit how you work.",
    body:
      "Use the columns button at the end of the header row to toggle optional fields like company, assignee, sales leader, status, and priority.",
    bullets: [
      "Date, action, and signal stay visible so you can always work the queue.",
      "Your choices are saved for next time.",
    ],
    icon: "fa-table-columns",
    accent: "var(--color-hotlist)",
    accentSoft: "rgba(var(--rgb-hotlist), 0.1)",
  },
  {
    id: "hotlist-table-rows",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.Sales_Home,
    target: TOUR_TARGET.HOTLIST_TABLE_ROW,
    placement: "right",
    tooltipSize: "compact",
    kicker: "Your queue",
    title: "Prioritized action rows",
    subtitle: "Email, call, and LinkedIn — ranked by urgency.",
    body:
      "Each row is a concrete next step on an account. On the next steps we'll open a sample action modal so you can see the layout.",
    bullets: [
      "Cellhire, Astound Broadband, and altafiber are sample accounts from your portfolio.",
      "Later you'll click any row to work it for real.",
    ],
    icon: "fa-table-list",
    accent: "var(--color-hotlist)",
    accentSoft: "rgba(var(--rgb-hotlist), 0.1)",
  },
  {
    id: "hotlist-action-context",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.Sales_Home,
    target: TOUR_TARGET.CAL_ACTION_INSIGHT,
    placement: "right",
    tooltipSize: "compact",
    skipScroll: true,
    kicker: "Action modal",
    title: "Insight context on the left",
    subtitle: "See why this action was recommended.",
    body:
      "When you open an action, the left panel shows the insight, contact, or asset behind the recommendation — so you always know the \"why\" before you reach out.",
    bullets: [
      "Account, industry, and contact insights each open here.",
      "Priority and action type appear in the header.",
      "Mark done when you've completed the touch.",
    ],
    icon: "fa-lightbulb",
    accent: "var(--color-hotlist)",
    accentSoft: "rgba(var(--rgb-hotlist), 0.1)",
  },
  {
    id: "hotlist-action-prompt",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.Sales_Home,
    target: TOUR_TARGET.CAL_ACTION_PROMPT,
    placement: "left",
    tooltipSize: "compact",
    skipScroll: true,
    kicker: "Suggested copy",
    title: "Outreach prompts on the right",
    subtitle: "Subject lines and messages ready to edit.",
    body:
      "Intentico drafts email, call, and LinkedIn copy based on the insight. Edit the text, copy it, or launch your mail client — then mark the action complete.",
    bullets: [
      "Prompts regenerate when the underlying insight changes.",
      "Leaders can approve actions before reps send.",
      "Series actions show step tabs for multi-touch cadences.",
    ],
    icon: "fa-pen-fancy",
    accent: "var(--color-hotlist)",
    accentSoft: "rgba(var(--rgb-hotlist), 0.1)",
  },
  {
    id: "hotlist-action-footer",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.Sales_Home,
    target: TOUR_TARGET.CAL_ACTION_FOOTER,
    placement: "top",
    tooltipSize: "compact",
    skipScroll: true,
    kicker: "Work the action",
    title: "Send, copy, or complete",
    subtitle: "Footer buttons finish the loop.",
    body:
      "Use Open in email, Copy message, or channel-specific CTAs to act on the recommendation. Mark done when finished so your Hotlist and digests stay accurate.",
    bullets: [
      "Batch mode lets you queue several actions in one session.",
      "Dismiss removes actions that aren't relevant.",
      "Completed actions feed activity history for leaders.",
    ],
    icon: "fa-paper-plane",
    accent: "var(--color-hotlist)",
    accentSoft: "rgba(var(--rgb-hotlist), 0.1)",
  },
  {
    id: "nav-accounts",
    roles: APP_ROLES,
    kind: "spotlight",
    target: TOUR_TARGET.NAV_ACCOUNTS,
    placement: "right",
    kicker: "Accounts",
    title: "Your portfolio lives here",
    subtitle: "Browse every relationship in one table.",
    body:
      "Accounts is your book of business — search, filter, assign owners, and open any company workspace. We'll visit the Accounts page on the next step.",
    bullets: [
      `Sample companies in the tour mirror ${TUTORIAL_SCENARIO.clientLabel}'s portfolio.`,
      "Press Next when you're ready to see the Accounts table.",
      "Create accounts here when you're an admin or leader with permission.",
    ],
    icon: "fa-building",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "accounts-portfolio",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.ALL_ACCOUNTS,
    target: TOUR_TARGET.ACCOUNTS_TOOLBAR,
    placement: "bottom",
    kicker: "Accounts",
    title: "Your portfolio masthead",
    subtitle: "Client context, counts, and list controls.",
    body:
      "This bar shows which organization you’re in, how many accounts match your view, and the tools to search or add companies. We’ll highlight search and create next.",
    bullets: [
      "Clear filters resets search and team filters without changing client.",
      "Row counts reflect your role — reps usually see assigned accounts only.",
    ],
    icon: "fa-building",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "accounts-search",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.ALL_ACCOUNTS,
    target: TOUR_TARGET.ACCOUNTS_SEARCH,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Accounts",
    title: "Search accounts",
    subtitle: "Find companies by name, domain, or industry.",
    body:
      "Open this control to filter the portfolio table. Results update as you type, with suggestions from accounts already loaded for this client.",
    bullets: [
      "Clear search from the popover when you want the full list again.",
      "Combine search with assignee, sales-leader, or tag filters in the table.",
    ],
    icon: "fa-magnifying-glass-plus",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "accounts-add",
    roles: LEADER_ROLES,
    kind: "spotlight",
    route: PATH.ALL_ACCOUNTS,
    target: TOUR_TARGET.ACCOUNTS_ADD,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Accounts",
    title: "Add an account",
    subtitle: "Create a company in this organization’s portfolio.",
    body:
      "Open Actions, then choose Add account. Leaders and admins can create accounts after selecting an organization in the masthead.",
    bullets: [
      "New accounts show up in the table for anyone with access to them.",
      "Sales reps typically don’t create accounts — they work the list they’re assigned.",
    ],
    icon: "fa-plus",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "accounts-open",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.ALL_ACCOUNTS,
    target: TOUR_TARGET.ACCOUNTS_TABLE_HEAD,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Account workspace",
    title: "Your sample portfolio table",
    subtitle: "Each row opens a dedicated account page.",
    body:
      "These are example companies from the tour scenario. In production, this table reflects your assigned accounts with insight counts, owners, and tags.",
    bullets: [
      "Next we'll expand a row for a Narrative radar summary.",
      "Leaders can also assign ownership from this table before opening an account.",
    ],
    icon: "fa-briefcase",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "accounts-row-expand",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.ALL_ACCOUNTS,
    target: TOUR_TARGET.ACCOUNTS_ROW_DETAIL,
    placement: "bottom",
    tooltipSize: "compact",
    spotlightClipHeight: 440,
    kicker: "Accounts",
    title: "Expand a row for details",
    subtitle: "Narrative radar without leaving the list.",
    body:
      "Expand a row to see that account's Narrative radar trend summary — key developments and strategic impact at a glance. If none is available yet, you'll see the company overview instead.",
    bullets: [
      "Click the chevron again to collapse the panel.",
      "Click the row itself (not the chevron) to open the account page.",
    ],
    icon: "fa-chevron-down",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "accounts-bulk-assign",
    roles: LEADER_ROLES,
    kind: "spotlight",
    route: PATH.ALL_ACCOUNTS,
    target: TOUR_TARGET.ACCOUNTS_BULK_ASSIGN,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Accounts",
    title: "Bulk-assign selected accounts",
    subtitle: "Update ownership across multiple companies at once.",
    body:
      "We’ve selected a sample account so the bulk bar appears. Select one or more rows with the checkboxes, then open Assign and choose Assign reps or Sales leader, when your role allows, to update who owns and manages them.",
    bullets: [
      "Sales leaders can assign reps on accounts they manage.",
      "Client admins and platform admins can also change sales leaders in bulk.",
    ],
    icon: "fa-users",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "accounts-row-assign",
    roles: LEADER_ROLES,
    kind: "spotlight",
    route: PATH.ALL_ACCOUNTS,
    target: TOUR_TARGET.ACCOUNTS_ROW_ASSIGN,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Accounts",
    title: "Assign from a single row",
    subtitle: "Same controls, one account at a time.",
    body:
      "On any row, open Assignee to set primary or secondary reps. If your role includes it, use the Sales leader cell to change who manages that account — without using bulk select.",
    bullets: [
      "Empty cells show Assign rep / Assign leader shortcuts.",
      "Filled cells stay clickable so you can edit the current owners.",
    ],
    icon: "fa-user-plus",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "accounts-enter",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insights"),
    target: TOUR_TARGET.ACCOUNT_MASTHEAD,
    placement: "bottom",
    kicker: "Account workspace",
    title: "Welcome to an account page",
    subtitle: "Everything for one relationship in one place.",
    body:
      "We're opening a sample account the way you normally land — on its signals. Name, domain, and view switches sit up top.",
    bullets: [
      "Sample data is labeled — your workspace may still be empty.",
      "Use the back button or Accounts nav to return to the portfolio.",
      "Next we’ll look at company info and tags, then the calendar.",
    ],
    icon: "fa-building-user",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-masthead-meta",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insights"),
    target: TOUR_TARGET.ACCOUNT_MASTHEAD_META,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Account workspace",
    title: "Company info and tags",
    subtitle: "Profile details and labels for this account.",
    body:
      "The info icon expands company context — size, description, and links. The tag icon lets you set priority and add labels so you can find and segment accounts later.",
    bullets: [
      "Info stays on the account page; expand it whenever you need background.",
      "Tags and priority apply to this company across your portfolio views.",
    ],
    icon: "fa-circle-info",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-calendar-tab",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("calendar"),
    target: TOUR_TARGET.ACCOUNT_CALENDAR_TAB,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Calendar",
    title: "This account’s calendar",
    subtitle: "Scheduled actions for this company only.",
    body:
      "This is your calendar view for this account. All scheduled actions related to this account show here — emails, calls, and LinkedIn steps tied to this relationship.",
    bullets: [
      "Actions here also sync with your Hotlist.",
      "Next we’ll look at one action card in the list.",
    ],
    icon: "fa-calendar-days",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-calendar-action",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("calendar"),
    target: TOUR_TARGET.ACCOUNT_CALENDAR_ACTION,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Calendar",
    title: "Open an action",
    subtitle: "Each card is a scheduled touch for this account.",
    body:
      "Click any action in the list to open it — the same work modal you’d use from the Hotlist, with the prompt, context, and complete/dismiss controls.",
    bullets: [
      "Channel and priority show on the card before you open it.",
      "Completing here updates the Hotlist too.",
    ],
    icon: "fa-bolt",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-calendar-view-toggle",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("calendar"),
    target: TOUR_TARGET.ACCOUNT_CALENDAR_VIEW_TOGGLE,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Calendar",
    title: "List or month grid",
    subtitle: "Switch how you scan this account’s actions.",
    body:
      "Flip between the event list and the month calendar. List is great for working through what’s due; the grid helps you see how the week fills in.",
    bullets: [
      "Use Jump to Today from the month grid to re-center quickly.",
      "Date-range filters apply to both views.",
    ],
    icon: "fa-table-columns",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-insights-tab",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insights"),
    target: TOUR_TARGET.ACCOUNT_INSIGHTS_TAB,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Insights",
    title: "This account’s insights",
    subtitle: "Research signals for this company only.",
    body:
      "This is your insights tab for this account. You land on Signals by default — the same place you’ll open in day-to-day work.",
    bullets: [
      "Priority moments and key signals for this company live here.",
      "You can switch to a classic table view anytime.",
    ],
    icon: "fa-lightbulb",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-signals-overview",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insights"),
    target: TOUR_TARGET.ACCOUNT_SIGNALS_NAV,
    placement: "right",
    tooltipSize: "compact",
    spotlightClipHeight: 320,
    kicker: "Signals",
    title: "Browse grouped signals",
    subtitle: "Different kinds of intel, one left rail.",
    body:
      "We’ve opened the first signal for you. Use this list to jump between Priority moments (high-value plays), Key signals (theme groups), and Other updates.",
    bullets: [
      "Selecting a signal fills in the detail pane on the right.",
      "Each signal may bundle multiple related events.",
    ],
    icon: "fa-layer-group",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-signals-actions",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insights"),
    target: TOUR_TARGET.ACCOUNT_SIGNALS_ACTIONS,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Signals",
    title: "Act on a signal",
    subtitle: "Share, complete, or dismiss from the detail header.",
    body:
      "These icons let you triage the signal: open a social prompt, mark it complete, or dismiss it if it isn’t useful.",
    bullets: [
      "They appear on Signals, tables, industry insights, assets, and more.",
      "Choose Outreach in the App Guide to open each flow in detail.",
    ],
    icon: "fa-share-nodes",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-insights-view-toggle",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insights"),
    target: TOUR_TARGET.ACCOUNT_INSIGHTS_VIEW_TOGGLE,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Insights",
    title: "Signals or table",
    subtitle: "Switch how you browse this account’s intel.",
    body:
      "Use this control to move between the Signals layout and the classic insights table — same insights, two ways to scan.",
    bullets: [
      "Signals groups themes and priority moments.",
      "Next we’ll open the table and walk the column headers.",
    ],
    icon: "fa-table-columns",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-insights-table",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.ACCOUNT_INSIGHTS_HEAD,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Insights table",
    title: "Account intelligence table",
    subtitle: "Sort and scan every open insight.",
    body:
      "The table lists research for this company with type, status, score, and date. Use the column headers to sort, and filters when you need a narrower slice.",
    bullets: [
      "Next we’ll expand a row for the full write-up.",
      "Social prompt, complete, and dismiss stay on every row.",
    ],
    icon: "fa-table",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-insights-row-expand",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.ACCOUNT_INSIGHTS_ROW_DETAIL,
    placement: "bottom",
    tooltipSize: "compact",
    spotlightClipHeight: 280,
    kicker: "Insights table",
    title: "Expand a row for details",
    subtitle: "Full synopsis without leaving the table.",
    body:
      "We’ve expanded a sample insight. Selecting a row reveals the title, synopsis, and notes so you can read deeper before you act.",
    bullets: [
      "Click the chevron again to collapse it.",
      "Scores and dates stay visible in the row above.",
    ],
    icon: "fa-chevron-down",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-insights-table-actions",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.ACCOUNT_INSIGHTS_TABLE_ACTIONS,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Insights table",
    title: "Act from any row",
    subtitle: "Share, complete, or dismiss here too.",
    body:
      "These same icons work in table view — open a social prompt, mark complete, or dismiss when you’re done with an insight.",
    bullets: [
      "They match the controls you saw on Signals.",
      "Choose Outreach in the App Guide for the full social-prompt and triage walkthrough.",
    ],
    icon: "fa-share-nodes",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-contacts-tab",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("contacts"),
    target: TOUR_TARGET.ACCOUNT_CONTACTS_TAB,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Contacts",
    title: "This account’s contacts",
    subtitle: "People tied to this company only.",
    body:
      "This is your contacts tab for this account. Everyone Intentico has found or you’ve added for this company shows here — ready to enrich, filter, and work.",
    bullets: [
      "Contact actions appear on your Hotlist when scheduled.",
      "Click a contact for full intelligence detail.",
    ],
    icon: "fa-address-card",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-contacts",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("contacts"),
    target: TOUR_TARGET.ACCOUNT_CONTACTS_HEAD,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Contacts",
    title: "People at this account",
    subtitle: "Enrich, filter, and work contact actions.",
    body:
      "The contacts table lists stakeholders Intentico has found or you've added. Next we’ll expand a row, then walk triage and reordering.",
    bullets: [
      "Contact actions appear on your Hotlist when scheduled.",
      "Use filters when the list gets long.",
    ],
    icon: "fa-address-card",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-contacts-row-expand",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("contacts"),
    target: TOUR_TARGET.ACCOUNT_CONTACTS_ROW_DETAIL,
    placement: "top",
    tooltipSize: "compact",
    spotlightClipHeight: 280,
    skipScroll: true,
    kicker: "Contacts",
    title: "Expand a contact for profile detail",
    subtitle: "Full intelligence without leaving the list.",
    body:
      "We’ve opened a sample contact. Expanding a row shows profile summaries and enrichment details so you can judge fit before you reach out.",
    bullets: [
      "Click the chevron or row again to collapse.",
      "Rows without extra profile data stay flat.",
    ],
    icon: "fa-chevron-down",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-contacts-actions",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("contacts"),
    target: TOUR_TARGET.ACCOUNT_CONTACTS_ACTIONS,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Contacts",
    title: "Favorite, complete, or dismiss",
    subtitle: "Triage people the same way you triage insights.",
    body:
      "Star contacts you want to keep close, mark them complete when you’ve worked them, or dismiss people who aren’t relevant to this account.",
    bullets: [
      "Favorites help you filter back to VIPs quickly.",
      "Dismissed contacts stay recoverable from the archive section.",
    ],
    icon: "fa-star",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-contacts-reorder",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("contacts"),
    target: TOUR_TARGET.ACCOUNT_CONTACTS_REORDER,
    placement: "right",
    tooltipSize: "compact",
    kicker: "Contacts",
    title: "Drag to reorder",
    subtitle: "Put your buyer's map in the order you want.",
    body:
      "Use the grip handle to drag contacts up or down. Your custom order sticks for this account so the shortlist stays aligned with how you sell.",
    bullets: [
      "Reordering is available when filters aren’t narrowing the list.",
      "Drag across rows to reshape your decision-maker order.",
    ],
    icon: "fa-grip-vertical",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "account-contacts-social",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("contacts"),
    target: TOUR_TARGET.ACCOUNT_CONTACTS_SOCIAL,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Contacts",
    title: "Open social prompts",
    subtitle: "Draft outreach from this person.",
    body:
      "The share icon also launches social prompts for this contact — LinkedIn messages and multi-step cadences grounded in account context.",
    bullets: [
      "Same workspace you open from insights, assets, and Hotlist.",
      "Choose Outreach in the App Guide to walk the full flow.",
    ],
    icon: "fa-share-nodes",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "outreach-icons",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.OUTREACH_SHARE,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Outreach & triage",
    title: "Open with the share icon",
    subtitle: "Same control wherever intel lives.",
    body:
      "This share icon appears on account Signals and tables, industry insights, assets, contacts, and Hotlist actions. Next we’ll open the outreach workspace it launches.",
    bullets: [
      "Complete and dismiss sit beside it — we’ll open those after social prompts.",
      "One set of icons, the same flows everywhere.",
    ],
    icon: "fa-share-nodes",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "social-prompt-sequence",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.SOCIAL_PROMPT_SEQUENCE,
    placement: "right",
    demoOverlay: "social-prompt",
    tooltipSize: "compact",
    skipScroll: true,
    spotlightClipHeight: 320,
    kicker: "Social prompts",
    title: "Pick a cadence step",
    subtitle: "The left rail walks the sequence.",
    body:
      "Each step is a channel in the cadence — email, LinkedIn, call, and so on. Select a step to load its draft in the center.",
    bullets: [
      "Completed steps show a Done tag.",
      "Multi-step sequences keep context as you move through them.",
    ],
    icon: "fa-list-ol",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "social-prompt-compose",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.SOCIAL_PROMPT_COMPOSE,
    placement: "left",
    demoOverlay: "social-prompt",
    tooltipSize: "compact",
    skipScroll: true,
    spotlightClipHeight: 280,
    kicker: "Social prompts",
    title: "Edit the drafted message",
    subtitle: "Copy ready for the selected step.",
    body:
      "The center panel is the message body for the step you selected. Edit it, then copy or open the channel when you’re ready to send.",
    bullets: [
      "Subject lines appear for email steps.",
      "LinkedIn and call steps swap in channel-specific drafts the same way.",
    ],
    icon: "fa-pen-fancy",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "social-prompt-contacts",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.SOCIAL_PROMPT_CONTACTS,
    placement: "left",
    demoOverlay: "social-prompt",
    tooltipSize: "compact",
    skipScroll: true,
    kicker: "Social prompts",
    title: "Choose who to send to",
    subtitle: "Recipient panel on the right.",
    body:
      "Pick the contact this step is for. People who don’t match the channel (no email, LinkedIn, or phone) stay grayed out so you don’t misfire.",
    bullets: [
      "The selected contact stays with the cadence as you walk steps.",
      "You can still copy or send manually if no contact is picked.",
    ],
    icon: "fa-user",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "social-prompt-footer",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.SOCIAL_PROMPT_FOOTER,
    placement: "top",
    demoOverlay: "social-prompt",
    tooltipSize: "compact",
    skipScroll: true,
    kicker: "Social prompts",
    title: "Send, copy, or complete",
    subtitle: "Action buttons finish the step.",
    body:
      "Use the footer to open email, copy the message, or mark the step complete when you’re done — then move on to the next cadence step.",
    bullets: [
      "Primary actions change with the channel (email, LinkedIn, call).",
      "Leaders may also see save and series controls here.",
    ],
    icon: "fa-paper-plane",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "social-prompt-request-more",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.SOCIAL_PROMPT_REQUEST_MORE,
    placement: "right",
    demoOverlay: "social-prompt",
    tooltipSize: "compact",
    skipScroll: true,
    kicker: "Social prompts",
    title: "Request more prompts",
    subtitle: "Need a fresh cadence? Start here.",
    body:
      "Request more opens the setup screen to generate another sequence for this insight — with optional title, voice, context, cadence, and recipient.",
    bullets: [
      "You’ll get a notification when the new prompts are ready.",
      "Same control wherever you open social prompts.",
    ],
    icon: "fa-plus",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "social-prompt-request-setup",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.SOCIAL_PROMPT_REQUEST_SETUP,
    placement: "right",
    demoOverlay: "social-prompt-request",
    tooltipSize: "compact",
    skipScroll: true,
    kicker: "Request sequence",
    title: "Title, voice, and context",
    subtitle: "Optional setup on the left.",
    body:
      "Name the sequence, pick a voice style, and add anything the AI should know — tone, angle, or deal specifics.",
    bullets: [
      "All fields are optional.",
      "Saved templates can pre-fill this panel when you have them.",
    ],
    icon: "fa-sliders",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "social-prompt-request-cadence",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.SOCIAL_PROMPT_REQUEST_CADENCE,
    placement: "left",
    demoOverlay: "social-prompt-request",
    tooltipSize: "compact",
    skipScroll: true,
    spotlightClipHeight: 320,
    kicker: "Request sequence",
    title: "Shape the cadence",
    subtitle: "Channels and order in the center.",
    body:
      "Add, remove, or reorder steps — email, LinkedIn, call, and more — so the new sequence matches how you want to follow up.",
    bullets: [
      "Save a cadence as a template for the next request.",
      "The first step’s channel also filters which recipients are available.",
    ],
    icon: "fa-list-ol",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "social-prompt-request-recipient",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.SOCIAL_PROMPT_REQUEST_RECIPIENT,
    placement: "left",
    demoOverlay: "social-prompt-request",
    tooltipSize: "compact",
    skipScroll: true,
    kicker: "Request sequence",
    title: "Pick a recipient",
    subtitle: "Optional — tailor the ask to someone.",
    body:
      "Select who this new sequence is for. Contacts that don’t match the first step’s channel stay grayed out.",
    bullets: [
      "You can still request without a contact.",
      "Recipient stays with the sequence once prompts arrive.",
    ],
    icon: "fa-user",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "social-prompt-request",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.SOCIAL_PROMPT_REQUEST,
    placement: "top",
    demoOverlay: "social-prompt-request",
    tooltipSize: "compact",
    skipScroll: true,
    kicker: "Request sequence",
    title: "Submit the request",
    subtitle: "Hit Request sequence when you’re ready.",
    body:
      "Submit sends the ask. You’ll get a notification when the new prompts are ready to open and send.",
    bullets: [
      "Same flow from insights, assets, contacts, and Hotlist.",
      "Back to prompts returns you to the current cadence anytime.",
    ],
    icon: "fa-paper-plane",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.08)",
  },
  {
    id: "insight-complete-icon",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.OUTREACH_COMPLETE,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Complete",
    title: "Mark complete from here",
    subtitle: "The check icon opens the complete dialog.",
    body:
      "When you’ve used an insight, tap complete. Next we’ll open the dialog so you can see activity logging and notes.",
    bullets: [
      "Same icon on Signals, industry insights, and related surfaces.",
      "Completed insights leave your homepage timeline.",
    ],
    icon: "fa-circle-check",
    accent: "var(--color-success)",
    accentSoft: "rgba(var(--rgb-completed), 0.1)",
  },
  {
    id: "insight-complete",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.INSIGHT_COMPLETE_MODAL,
    placement: "right",
    demoOverlay: "complete-insight",
    tooltipSize: "compact",
    tooltipPreferSide: true,
    skipScroll: true,
    spotlightPad: 2,
    kicker: "Complete",
    title: "Mark an insight complete",
    subtitle: "Log what you did, then clear it from the active list.",
    body:
      "Complete lets you record emails, calls, and LinkedIn touches, add an optional note, and remove the insight from your homepage timeline — it still stays on account and industry pages.",
    bullets: [
      "Use this after you’ve acted on the signal.",
      "The same dialog opens from every complete icon in the app.",
    ],
    icon: "fa-circle-check",
    accent: "var(--color-success)",
    accentSoft: "rgba(var(--rgb-completed), 0.1)",
  },
  {
    id: "insight-dismiss-icon",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.OUTREACH_DISMISS,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Dismiss",
    title: "Dismiss from the X icon",
    subtitle: "Opens the permanent dismiss confirm.",
    body:
      "When an insight isn’t relevant, dismiss it here. Next we’ll open that dialog so you can see the warning and feedback field.",
    bullets: [
      "Prefer complete when you used the insight.",
      "Same dismiss control everywhere the icon appears.",
    ],
    icon: "fa-circle-xmark",
    accent: "var(--color-danger-strong)",
    accentSoft: "color-mix(in srgb, var(--color-danger-strong) 10%, transparent)",
  },
  {
    id: "insight-dismiss",
    roles: APP_ROLES,
    kind: "spotlight",
    route: getTutorialDemoAccountPath("insightsTable"),
    target: TOUR_TARGET.INSIGHT_DISMISS_MODAL,
    placement: "right",
    demoOverlay: "dismiss-insight",
    tooltipSize: "compact",
    tooltipPreferSide: true,
    skipScroll: true,
    spotlightPad: 2,
    kicker: "Dismiss",
    title: "Dismiss what isn’t useful",
    subtitle: "Permanent — so leave a quick reason when you can.",
    body:
      "Dismiss removes the insight from your homepage and other pages. Optional comments help Intentico improve future recommendations.",
    bullets: [
      "Prefer complete when you used the insight; dismiss when it wasn’t relevant.",
      "You’ll see the same confirm dialog on every dismiss icon.",
    ],
    icon: "fa-circle-xmark",
    accent: "var(--color-danger-strong)",
    accentSoft: "color-mix(in srgb, var(--color-danger-strong) 10%, transparent)",
  },
  {
    id: "nav-insights",
    roles: APP_ROLES,
    kind: "spotlight",
    target: TOUR_TARGET.NAV_INSIGHTS,
    placement: "right",
    kicker: "Insights",
    title: "Your market context",
    subtitle: "Trends beyond a single account.",
    body:
      "Insights surfaces market and industry signals you can bring into any conversation. Open Insights to explore the signals available to your workspace.",
    bullets: [
      "Follow topics, target industries, and competitor moves.",
      "Search and filter insights to find relevant developments.",
      "Pair insights with Hotlist actions for timely outreach.",
    ],
    icon: "fa-lightbulb",
    accent: "var(--industry-insight-color)",
    accentSoft: "rgba(var(--rgb-industry), 0.08)",
  },
  {
    id: "insights-overview", bullets: [], roles: [USER_ROLE.ADMIN], kind: "spotlight", route: PATH.INDUSTRY_INSIGHTS,
    target: "insights-workspace", placement: "bottom", tooltipSize: "compact",
    kicker: "Insights", title: "Know what matters today", subtitle: "Topics, target industries, and competitor moves.",
    body: "Today's essentials headline the important developments. Explore a trend below to see its sources, draft your point of view, or prepare a conversation.",
    icon: "fa-lightbulb", accent: "var(--industry-insight-color)", accentSoft: "rgba(var(--rgb-industry), 0.08)",
  },
  {
    id: "insights-library", bullets: [], roles: [USER_ROLE.ADMIN], kind: "spotlight", route: `${PATH.INDUSTRY_INSIGHTS}/all`,
    target: "insights-workspace", placement: "bottom", tooltipSize: "compact",
    kicker: "All insights", title: "Find every individual insight", subtitle: "Includes insights that are not part of a trend.",
    body: "Search and filter the library. Open a headline for its summary, original sources, related trends, and the existing social prompt workspace. Save useful insights to return to them later.",
    icon: "fa-list", accent: "var(--industry-insight-color)", accentSoft: "rgba(var(--rgb-industry), 0.08)",
  },
  {
    id: "insights-competitors", bullets: [], roles: [USER_ROLE.ADMIN], kind: "spotlight", route: `${PATH.INDUSTRY_INSIGHTS}/competitors`,
    target: "insights-workspace", placement: "bottom", tooltipSize: "compact",
    kicker: "Competitors", title: "Follow the companies buyers compare", subtitle: "Positioning and recent public moves.",
    body: "Expand a company to see its supporting insights. Managers can review suggested companies, edit monitoring, and manage the sources used for each company.",
    icon: "fa-flag", accent: "var(--industry-insight-color)", accentSoft: "rgba(var(--rgb-industry), 0.08)",
  },
  {
    id: "nav-assets",
    roles: APP_ROLES,
    kind: "spotlight",
    target: TOUR_TARGET.NAV_ASSETS,
    placement: "right",
    kicker: "Assets",
    title: "Approved collateral",
    subtitle: "Files ready to share with prospects.",
    body:
      "Assets stores decks, one-pagers, and other materials your marketing team has approved. We'll open the library on the next step.",
    bullets: [
      "Sample files appear during the tour.",
      "Press Next to see the assets table.",
      "Attach assets when an action calls for supporting material.",
    ],
    icon: "fa-folder-open",
    accent: "var(--asset-insight-color)",
    accentSoft: "rgba(var(--rgb-asset), 0.1)",
  },
  {
    id: "assets-library",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.MY_ASSETS,
    target: TOUR_TARGET.ASSETS_TABLE_HEAD,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Assets",
    title: "Your content library",
    subtitle: "Upload, preview, and organize files.",
    body:
      "The assets table lists everything available to your org. Click a file to preview or download, and use categories to keep large libraries manageable.",
    bullets: [
      "Upload adds new collateral when your role allows it.",
      "Bulk actions help admins maintain the library.",
      "Next we'll expand a row to see the full summary.",
    ],
    icon: "fa-file-lines",
    accent: "var(--asset-insight-color)",
    accentSoft: "rgba(var(--rgb-asset), 0.1)",
  },
  {
    id: "assets-row-expand",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.MY_ASSETS,
    target: TOUR_TARGET.ASSETS_ROW_DETAIL,
    placement: "bottom",
    tooltipSize: "compact",
    spotlightClipHeight: 280,
    kicker: "Assets",
    title: "Expand a row for details",
    subtitle: "Click any asset to open metadata and summary.",
    body:
      "Selecting a row shows upload info, size, status, and the full summary or key points — without leaving your library list.",
    bullets: [
      "Click the row or chevron again to collapse it.",
      "Use this when you need more than the short description preview.",
    ],
    icon: "fa-chevron-down",
    accent: "var(--asset-insight-color)",
    accentSoft: "rgba(var(--rgb-asset), 0.1)",
  },
  {
    id: "assets-social-prompt",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.MY_ASSETS,
    target: TOUR_TARGET.ASSETS_SOCIAL_PROMPT,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Outreach",
    title: "Share an asset in a social prompt",
    subtitle: "Turn approved collateral into drafted posts.",
    body:
      "The share icon on each row opens social prompts for that asset — so your team can promote decks and one-pagers with ready-to-edit copy.",
    bullets: [
      "Same share control as insights and accounts.",
      "Choose Outreach in the App Guide for the full compose and request walkthrough.",
    ],
    icon: "fa-share-nodes",
    accent: "var(--asset-insight-color)",
    accentSoft: "rgba(var(--rgb-asset), 0.1)",
  },
  {
    id: "assets-add",
    roles: ADMIN_ROLES,
    kind: "spotlight",
    route: PATH.MY_ASSETS,
    target: TOUR_TARGET.ASSETS_ADD,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "For admins",
    title: "Add assets to the library",
    subtitle: "Upload new collateral for your org.",
    body:
      "Admins and client admins can add files or links from this masthead control. New assets become available to the team for outreach and account work.",
    bullets: [
      "Choose a category so the library stays easy to filter.",
      "Reps and leaders can browse and share assets without upload access.",
    ],
    icon: "fa-plus",
    accent: "var(--asset-insight-color)",
    accentSoft: "rgba(var(--rgb-asset), 0.1)",
  },
  {
    id: "nav-settings",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.SETTINGS,
    target: TOUR_TARGET.NAV_SETTINGS,
    placement: "right",
    tooltipSize: "compact",
    kicker: "Settings",
    title: "Personalize Intentico",
    subtitle: "Preferences — and org admin tools when you have them.",
    body:
      "Settings is where you tune digests, notifications, and outreach to how you work. We'll open the page next.",
    bullets: [
      "Everyone manages their own preference rows.",
      "Client admins and platform admins also see an Organization block below.",
    ],
    icon: "fa-gear",
    accent: "var(--secondary-navy-blue-color)",
    accentSoft: "rgba(var(--rgb-navigation), 0.08)",
  },
  {
    id: "settings-quick-actions",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.SETTINGS,
    target: TOUR_TARGET.SETTINGS_QUICK_ACTIONS,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Settings",
    title: "Help & troubleshooting",
    subtitle: "Shortcuts for help, cache, messages, and status.",
    body:
      "Open Help & troubleshooting under Resources to find guides, clear local cache, restore dismissed in-app messages, or check system status.",
    bullets: [
      "Use Clear cache if data looks stale or sync feels stuck.",
      "Help and Status open outside this page when you need docs or uptime info.",
    ],
    icon: "fa-bolt",
    accent: "var(--secondary-navy-blue-color)",
    accentSoft: "rgba(var(--rgb-navigation), 0.08)",
  },
  {
    id: "settings-preferences",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.SETTINGS,
    target: TOUR_TARGET.SETTINGS_PREFERENCES_GROUP,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Settings",
    title: "Your preferences",
    subtitle: "Personal controls, organized in one place.",
    body:
      "Use this navigation group to manage your digests, notifications, outreach voice, and cadence templates. Each setting opens in a focused panel.",
    bullets: [
      "We’ll highlight each preference next.",
      "Select a setting to open its controls.",
    ],
    icon: "fa-sliders",
    accent: "var(--color-sky)",
    accentSoft: "rgba(var(--rgb-sky), 0.08)",
  },
  {
    id: "settings-digest",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.SETTINGS,
    target: TOUR_TARGET.SETTINGS_DIGEST,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Your preferences",
    title: "Digest preferences",
    subtitle: "Your email summary of Hotlist work.",
    body:
      "Set how often you get digest email and which priority of actions is included — so the summary matches how you work.",
    bullets: [
      "Select Email digest to edit frequency and thresholds.",
      "Org-level digest delivery (for admins) lives separately under Organization.",
    ],
    icon: "fa-envelope-open-text",
    accent: "var(--color-sky)",
    accentSoft: "rgba(var(--rgb-sky), 0.08)",
  },
  {
    id: "settings-notifications",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.SETTINGS,
    target: TOUR_TARGET.SETTINGS_NOTIFICATIONS,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Your preferences",
    title: "Notification preferences",
    subtitle: "In-product and email alerts.",
    body:
      "Choose which assignment, asset, and account events ping you so important updates don’t get lost.",
    bullets: [
      "Select Notifications to opt in or out of alert types.",
    ],
    icon: "fa-bell",
    accent: "var(--color-sky)",
    accentSoft: "rgba(var(--rgb-sky), 0.08)",
  },
  {
    id: "settings-outreach",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.SETTINGS,
    target: TOUR_TARGET.SETTINGS_OUTREACH,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Your preferences",
    title: "Outreach preferences",
    subtitle: "Voice and context for AI drafts.",
    body:
      "Tune how Intentico writes email, call, and LinkedIn prompts for you — tone and context follow you into Hotlist and Accounts.",
    bullets: [
      "Select Outreach preferences to set voice per channel.",
    ],
    icon: "fa-comments",
    accent: "var(--color-sky)",
    accentSoft: "rgba(var(--rgb-sky), 0.08)",
  },
  {
    id: "settings-cadence-templates",
    roles: APP_ROLES,
    kind: "spotlight",
    route: PATH.SETTINGS,
    target: TOUR_TARGET.SETTINGS_CADENCE_TEMPLATES,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Your preferences",
    title: "Cadence templates",
    subtitle: "Reusable plans for Request Outreach Prompts.",
    body:
      "Save cadence plans here so you can prefill multi-step outreach instead of rebuilding the same sequence each time.",
    bullets: [
      "Select Cadence templates to create or edit templates.",
      "Use them from Accounts when you request social prompts.",
    ],
    icon: "fa-layer-group",
    accent: "var(--color-sky)",
    accentSoft: "rgba(var(--rgb-sky), 0.08)",
  },
  {
    id: "settings-organization",
    roles: ADMIN_ROLES,
    kind: "spotlight",
    route: PATH.SETTINGS,
    target: TOUR_TARGET.SETTINGS_ORGANIZATION_GROUP,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Settings",
    title: "Organization",
    subtitle: "Admin controls for your organization.",
    body:
      "Choose the organization workspace once at the top of Settings. That selection carries across team management, access, digest defaults and recipients, and integrations. Billing stays with your primary organization. Client setup lives under Platform administration.",
    bullets: [
      "We’ll call out the admin settings that apply to your role next.",
      "The Admin badge identifies settings that affect your organization or platform.",
    ],
    icon: "fa-building",
    accent: "var(--color-violet)",
    accentSoft: "rgba(var(--rgb-violet), 0.1)",
  },
  {
    id: "settings-client-setup",
    roles: [USER_ROLE.ADMIN],
    kind: "spotlight",
    route: PATH.SETTINGS,
    target: TOUR_TARGET.SETTINGS_CLIENT_SETUP,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Organization",
    title: "Client setup",
    subtitle: "Platform-wide onboarding and billing.",
    body:
      "As a platform admin, create clients, check readiness, and jump into subscriptions, Stripe, and limits from this section.",
    bullets: [
      "This hub is for Intentico operators — not client-admin self-serve billing.",
    ],
    icon: "fa-building-circle-check",
    accent: "var(--color-violet)",
    accentSoft: "rgba(var(--rgb-violet), 0.1)",
  },
  {
    id: "settings-billing",
    roles: [USER_ROLE.CLIENT_ADMIN],
    kind: "spotlight",
    route: PATH.SETTINGS,
    target: TOUR_TARGET.SETTINGS_BILLING,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Organization",
    title: "Billing & subscription",
    subtitle: "Plan, trial, and payment for your org.",
    body:
      "Client admins manage subscription tier, trial status, and payment here without leaving Intentico.",
    bullets: [
      "Select Billing & subscription to upgrade or review billing details.",
    ],
    icon: "fa-credit-card",
    accent: "var(--action-dismissed)",
    accentSoft: "color-mix(in srgb, var(--action-dismissed) 10%, transparent)",
  },
  {
    id: "settings-org-digest",
    roles: ADMIN_ROLES,
    kind: "spotlight",
    route: PATH.SETTINGS,
    target: TOUR_TARGET.SETTINGS_ORG_DIGEST,
    placement: "top",
    tooltipSize: "compact",
    kicker: "Organization",
    title: "Organization digest",
    subtitle: "Defaults for everyone in the org.",
    body:
      "Set delivery mode and default priority thresholds that apply across the organization — separate from each user’s personal digest preferences above.",
    bullets: [
      "Use this when digests should be admin-managed or org-wide defaults need a change.",
    ],
    icon: "fa-building",
    accent: "var(--color-violet)",
    accentSoft: "rgba(var(--rgb-violet), 0.1)",
  },
  {
    id: "settings-access",
    roles: ADMIN_ROLES,
    kind: "spotlight",
    route: PATH.SETTINGS,
    target: TOUR_TARGET.SETTINGS_ACCESS,
    placement: "top",
    tooltipSize: "compact",
    kicker: "Organization",
    title: "Access & sign-in",
    subtitle: "Invites, roles, and authentication.",
    body:
      "Invite teammates, set roles, and configure how users sign in — including SSO when your org uses it.",
    bullets: [
      "Select Access & sign-in to manage whitelist, invites, and sign-in policy.",
    ],
    icon: "fa-shield-halved",
    accent: "var(--color-violet)",
    accentSoft: "rgba(var(--rgb-violet), 0.1)",
  },
  {
    id: "settings-team",
    roles: ADMIN_ROLES,
    kind: "spotlight",
    route: PATH.SETTINGS,
    target: TOUR_TARGET.SETTINGS_TEAM,
    placement: "bottom",
    tooltipSize: "compact",
    kicker: "Organization",
    title: "Team management",
    subtitle: "Map reps to sales leaders.",
    body:
      "Assign sales reps to leaders so Hotlist and Accounts filters match your reporting lines.",
    bullets: [
      "Keep assignments current as people join, leave, or change managers.",
    ],
    icon: "fa-users-gear",
    accent: "var(--color-violet)",
    accentSoft: "rgba(var(--rgb-violet), 0.1)",
  },
  {
    id: "finish",
    roles: APP_ROLES,
    kind: "center",
    kicker: "You're ready",
    title: "Go win the week",
    subtitle: "Hotlist → Accounts → act on insights.",
    body:
      "You know where to find your daily actions, work them in the action modal, explore account workspaces, and adjust settings. Intentico will keep surfacing what matters — your job is to work the queue.",
    bullets: [
      "Start tomorrow on the Hotlist — click a row to open the action modal.",
      "Dive into any account for insights, contacts, and social prompts.",
      "Reopen this guide anytime from App Guide in the sidebar.",
    ],
    icon: "fa-rocket",
    accent: "var(--account-insight-color)",
    accentSoft: "rgba(var(--rgb-account), 0.1)",
  },
];

export type TutorialSectionId =
  | "full"
  | "hotlist"
  | "accounts"
  | "outreach"
  | "insights"
  | "assets"
  | "settings";

export type TutorialSection = {
  id: TutorialSectionId;
  label: string;
  description: string;
  icon: string;
  /** Ordered step ids for this path. `full` concatenates every other section’s stepIds. */
  stepIds?: TutorialStepId[];
};

/** Chooseable App Guide paths shown on the welcome screen. */
export const TUTORIAL_SECTIONS: TutorialSection[] = [
  {
    id: "full",
    label: "Full tour",
    description: "Every topic in order — same steps as each section, back to back.",
    icon: "fa-route",
  },
  {
    id: "hotlist",
    label: "Hotlist",
    description: "Daily actions, the action modal, and team filters.",
    icon: "fa-bolt",
    stepIds: [
      "nav-hotlist",
      "hotlist-actions",
      "team-rep-filter",
      "hotlist-schedule",
      "hotlist-view-switch",
      "hotlist-work-action",
      "hotlist-column-picker",
      "hotlist-table-rows",
      "hotlist-action-context",
      "hotlist-action-prompt",
      "hotlist-action-footer",
    ],
  },
  {
    id: "accounts",
    label: "Accounts",
    description: "Portfolio list, account workspace, insights, and contacts.",
    icon: "fa-building",
    stepIds: [
      "nav-accounts",
      "accounts-portfolio",
      "accounts-search",
      "accounts-add",
      "accounts-open",
      "accounts-row-expand",
      "accounts-bulk-assign",
      "accounts-row-assign",
      "accounts-enter",
      "account-masthead-meta",
      "account-calendar-tab",
      "account-calendar-action",
      "account-calendar-view-toggle",
      "account-insights-tab",
      "account-signals-overview",
      "account-signals-actions",
      "account-insights-view-toggle",
      "account-insights-table",
      "account-insights-row-expand",
      "account-insights-table-actions",
      "account-contacts-tab",
      "account-contacts",
      "account-contacts-row-expand",
      "account-contacts-actions",
      "account-contacts-reorder",
      "account-contacts-social",
    ],
  },
  {
    id: "outreach",
    label: "Outreach & triage",
    description: "Social prompts, request sequences, and complete / dismiss.",
    icon: "fa-share-nodes",
    stepIds: [
      "outreach-icons",
      "social-prompt-sequence",
      "social-prompt-compose",
      "social-prompt-contacts",
      "social-prompt-footer",
      "social-prompt-request-more",
      "social-prompt-request-setup",
      "social-prompt-request-cadence",
      "social-prompt-request-recipient",
      "social-prompt-request",
      "insight-complete-icon",
      "insight-complete",
      "insight-dismiss-icon",
      "insight-dismiss",
    ],
  },
  {
    id: "insights",
    label: "Insights",
    description: "Essentials, the full library, and competitors.",
    icon: "fa-lightbulb",
    stepIds: [
      "nav-insights",
      "insights-overview",
      "insights-library",
      "insights-competitors",
    ],
  },
  {
    id: "assets",
    label: "Assets",
    description: "Library browsing, share icons, and uploads.",
    icon: "fa-folder-open",
    stepIds: [
      "nav-assets",
      "assets-library",
      "assets-row-expand",
      "assets-social-prompt",
      "assets-add",
    ],
  },
  {
    id: "settings",
    label: "Settings",
    description: "Personal preferences and organization admin tools.",
    icon: "fa-gear",
    stepIds: [
      "nav-settings",
      "settings-quick-actions",
      "settings-preferences",
      "settings-digest",
      "settings-notifications",
      "settings-outreach",
      "settings-cadence-templates",
      "settings-organization",
      "settings-client-setup",
      "settings-billing",
      "settings-org-digest",
      "settings-access",
      "settings-team",
    ],
  },
];

/**
 * Build the runnable step list for a chosen section.
 * Welcome is handled by the section picker; finish is always appended when present for the role.
 * Full tour concatenates every topic section in picker order (Hotlist → … → Settings).
 */
export function buildTutorialStepsForSection(
  roleSteps: TutorialStep[],
  sectionId: TutorialSectionId,
): TutorialStep[] {
  const byId = new Map(roleSteps.map((step) => [step.id, step]));
  const finish = byId.get("finish");
  const section = TUTORIAL_SECTIONS.find((row) => row.id === sectionId);

  let core: TutorialStep[];
  if (sectionId === "full") {
    const seen = new Set<TutorialStepId>();
    core = [];
    for (const topic of TUTORIAL_SECTIONS) {
      if (topic.id === "full" || !topic.stepIds?.length) continue;
      for (const id of topic.stepIds) {
        if (seen.has(id)) continue;
        const step = byId.get(id);
        if (!step) continue;
        seen.add(id);
        core.push(step);
      }
    }
  } else if (!section?.stepIds?.length) {
    core = roleSteps.filter((step) => step.id !== "welcome" && step.id !== "finish");
  } else {
    core = section.stepIds
      .map((id) => byId.get(id))
      .filter((step): step is TutorialStep => Boolean(step));
  }

  return finish ? [...core, finish] : core;
}

export function getTutorialStepsForRole(role: USER_ROLE): TutorialStep[] {
  return TUTORIAL_STEPS.filter((step) => step.roles.includes(role));
}

/** Sections that still have at least one runnable step after role/capability filtering. */
export function getTutorialSectionsForSteps(availableSteps: TutorialStep[]): TutorialSection[] {
  const availableIds = new Set(availableSteps.map((step) => step.id));
  const topicHasSteps = (section: TutorialSection) =>
    Boolean(section.stepIds?.some((id) => availableIds.has(id)));

  return TUTORIAL_SECTIONS.filter((section) => {
    if (section.id === "full") {
      return TUTORIAL_SECTIONS.some(
        (topic) => topic.id !== "full" && topicHasSteps(topic)
      );
    }
    if (!section.stepIds?.length) return false;
    return topicHasSteps(section);
  });
}

export function getTutorialSectionsForRole(role: USER_ROLE): TutorialSection[] {
  return getTutorialSectionsForSteps(getTutorialStepsForRole(role));
}

export function getRoleTutorialLabel(role: USER_ROLE): string {
  switch (role) {
    case USER_ROLE.CLIENT_ADMIN:
      return "Client Admin";
    case USER_ROLE.SALES_LEADER:
      return "Sales Leader";
    case USER_ROLE.ADMIN:
      return "Administrator";
    case USER_ROLE.USER:
      return "User";
    default:
      return "Sales Rep";
  }
}

export function tourTargetSelector(target: TourTarget): string {
  return `[data-tour="${target}"]`;
}
